class SizeOverlay extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.selectedOption = null;
    this.noDefaultSelected = undefined;
    this.postCartAction = 'none';
  }
  connectedCallback() {
    this.init();
    this.postCartAction = this.dataset.postCartAction;
  }

  init() {
    this.noDefaultSelected = Boolean(
      this.querySelector('.size-overlay').dataset?.noDefaultSelected === 'true',
    );
    this.productItem = this.closest('.product-item');

    this.addEventListener('click', function (e) {
      const target = e.target;
      const sizeOption = target.closest('.size-overlay .size-option');
      const variantRadiosList =
        this.closest('.product-item')?.querySelectorAll('variant-radios');
      const variantRadios = Array.from(variantRadiosList || []).find(
        (el) => el.offsetParent !== null, // true only if the element is actually visible/rendered
      );

      if (!sizeOption) return;

      e.preventDefault();

      if (sizeOption.classList.contains('sold-out')) {
        // add the sold out badge
        if (variantRadios && variantRadios?.toogleOOSBadge) {
          variantRadios.toogleOOSBadge(true);
        }
        return;
      }

      if (this.noDefaultSelected && variantRadios) {
        /**
         * Requirements:
         * 1. Highlight the selected size.
         * 2. Update the colour swatches based on availability of the selected size.
         * 3. Strike out colours where the selected size is unavailable.
         * 4. If theres a selected color swatch do not update the size option availablity
         */

        // removed oos badge if present
        variantRadios?.toogleOOSBadge(false);

        // check if there's an active color swatch
        const hasActiveColorSwatch = variantRadios.querySelector('.active');
        //clear focus state
        this.clearFocusState();

        // set the focus state to the newly selected option
        this.setFocusState(sizeOption, true);

        // set the selectedOption state
        this.selectedOption = sizeOption;

        if (hasActiveColorSwatch) {
          // add to cart
          this.processSizeOptionSelection(sizeOption);
        } else {
          if (variantRadios?.updateColorSwatchState) {
            // update variant radios color swatch state depending on the passed variant id
            variantRadios.updateColorSwatchState(sizeOption.dataset.variantId);
          }
        }
      } else {
        this.processSizeOptionSelection(sizeOption);
      }
    });
  }

  processSizeOptionSelection(sizeOption) {
    const variantRadiosList =
      this.closest('.product-item')?.querySelectorAll('variant-radios');
    const variantRadios = Array.from(variantRadiosList || []).find(
      (el) => el.offsetParent !== null, // true only if the element is actually visible/rendered
    );

    variantRadios?.toogleOOSBadge(false);

    const variantId = sizeOption.dataset.variantId;
    if (!variantId) return;

    this.addToCart(variantId, sizeOption);
  }

  /**
   * Clear the strike-through on size option
   */
  clearOptionAvailabilityState() {
    this.querySelectorAll('.size-overlay .size-option').forEach(
      (sizeOption) => {
        sizeOption.classList.remove('sold-out');
      },
    );
  }

  clearFocusState(clearColorSwatchSelected = false) {
    // remove selected option
    this.selectedOption = null;

    // clear selected ui
    this.querySelectorAll('.size-overlay .size-option').forEach(
      (sizeOption) => {
        sizeOption.classList.remove('selected');
      },
    );

    if (clearColorSwatchSelected) {
      const variantRadiosList =
        this.closest('.product-item')?.querySelectorAll('variant-radios');
      const variantRadios = Array.from(variantRadiosList || []).find(
        (el) => el.offsetParent !== null, // true only if the element is actually visible/rendered
      );

      if (variantRadios) {
        // clear also the selected color
        variantRadios
          .querySelectorAll('.product__color-swatch')
          .forEach((swatch) => {
            // remove active class
            swatch.classList.remove('active');
          });
      }
    }
  }

  setFocusState(sizeOptionEl, focus = false) {
    if (sizeOptionEl) {
      if (focus) {
        // add focus class
        sizeOptionEl.classList.add('selected');
      } else {
        // remove focus class
        sizeOptionEl.classList.remove('selected');
      }
    }
  }

  constructToastContent({ quantity = 1, sizeOption, variantId }) {
    /**
     * Set the `width` query param on an image URL, only if it already has one.
     * Returns the original URL untouched if there's no width param or it can't be parsed.
     */
    function setImageWidth(url, width = 200) {
      try {
        const parsed = new URL(url, window.location.origin);
        const params = parsed.searchParams;

        if (!params.has('width')) return url;

        // If a height is also set, scale it proportionally so the image isn't cropped or squashed
        const oldWidth = Number(params.get('width'));
        const oldHeight = Number(params.get('height'));
        if (params.has('height') && oldWidth > 0 && oldHeight > 0) {
          params.set('height', Math.round((oldHeight / oldWidth) * width));
        }

        params.set('width', width);
        return parsed.toString();
      } catch {
        return url;
      }
    }

    // get the selected color swatch dataset and apply color name and selected image if present
    const productItem = this.closest('.product-item');
    const productTitle = this.dataset.productTitle || '';
    const variantRadios = productItem.querySelector('variant-radios');
    const options = variantRadios?.querySelector('script');
    const selectedColorSwatch = variantRadios?.querySelector('.active');

    let productImgUrl = this.dataset.productImgUrl || ''; // default fallback image
    let selecteOptions = {};

    if (options) {
      let thumbnail;

      const matchVariantObject = JSON.parse(options.innerHTML)?.find(
        (variant) => variant.id === Number(variantId),
      );
      // check if there's an image with the selected color swatch
      if (selectedColorSwatch && selectedColorSwatch?.dataset?.image) {
        thumbnail = selectedColorSwatch.dataset.image;
      } else if (matchVariantObject?.featured_media?.src) {
        // check if there's an image with the selected variant object
        thumbnail = matchVariantObject?.featured_media?.src;
      }

      if (thumbnail) {
        productImgUrl = setImageWidth(thumbnail, 200); // assign variant image url as the new thumbnail
      }

      selecteOptions = (matchVariantObject?.options ?? []).reduce(
        (acc, option, index) => {
          acc[index] = option;
          return acc;
        },
        {},
      );
    } else {
      selecteOptions = { size: sizeOption.dataset.size };
    }

    const innerHTML = `<div class="w-full relative">
      <div class="flex items-center gap-4 w-full">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-icon lucide-check"><path d="M20 6 9 17l-5-5"/></svg>
        <h5>Added to Cart</h5>
      </div>
      <div class="flex w-full relative gap-custom items-center" style="--gap: 12">
        <div class="product-img-wrapper">
          <img src="${productImgUrl}" alt="product-img" width="120" height="120" />
        </div>
        <div class="flex-1 relative">
          <p class="title">${productTitle}</p>
          <div class="w-full relative flex items-end justify-content gap-10">
            <div class="flex-1">
              <p class="product-selected-options">${Object.values(
                selecteOptions,
              )
                .map((option) => option)
                .join(', ')}</p>
            </div>
            <span class="product-qty">QTY: ${quantity}</span>
          </div>
        </div>
      </div>
    </div>`;

    return innerHTML;
  }

  addToCart(variantId, sizeOption) {
    if (this.isLoading) return;

    this.isLoading = true;
    // display loader
    this.toogleLoading(sizeOption, true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: this.getSectionsToRender().map((section) => section.id),
      }),
    })
      .then((res) => res.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        // Fetch updated cart data
        return fetch('/cart.json')
          .then((res) => res.json())
          .then((cart) => {
            // save cart items locally/remotely when logged-in
            if (cart?.items) {
              CartSyncController.saveItems(cart.items);
            }

            // Update cart count and related elements
            if (cart.item_count !== undefined) {
              CartUtils.updateCartCount(cart.item_count);
              CartUtils.updateHeaderTotalPrice(cart);
              CartUtils.updateFreeShippingBar(cart.items_subtotal_price);
            }
            return { parsedState, cart };
          })
          .then(({ parsedState }) => {
            // Use CartUtils for UI update
            if (!parsedState.errors) {
              // handle post add to cart action
              switch (this.postCartAction) {
                case 'open_minicart':
                  const cartInstance = document.querySelector('minicart-form');
                  // update the quick cart contents
                  CartUtils.updateMinicartUI({
                    parsedState,
                    sectionsToRender: this.getSectionsToRender(),
                    cartInstance: cartInstance,
                    totalsSelector: '.cart-info .totals',
                  });

                  // open the quick cart drawer
                  const cartNotification =
                    document.querySelector('cart-notification');

                  if (cartNotification?.open) {
                    cartNotification.open();
                  }
                  break;
                case 'show_toast':
                  const html = this.constructToastContent({
                    quantity: 1,
                    sizeOption,
                    variantId,
                  });
                  CartUtils.showAddToCartToast(html);
                  break;
                default:
                  // none -> do nothing
                  return;
              }
            } else {
              // todo error handlers
              CartUtils.showToast(
                'Cart Error Occurred while selecting product size. See logs for more info!',
                'error',
              );
              console.error(
                'Cart Error' + JSON.stringify(parsedState.errors, null, 2),
              );
            }
          });
      })
      .catch((err) => console.error('Add to cart error:', err))
      .finally(() => {
        this.isLoading = false;
        // display loader
        this.toogleLoading(sizeOption, false);

        if (this.noDefaultSelected) {
          // clear selection after being added to cart
          this.clearFocusState(true);
        }
      });
  }

  toogleLoading(option, loading = true) {
    if (!option) return;

    const label = option.querySelector('.size-label');
    const loader = option.querySelector('.icon-loader');

    // removed focus state e.g. border
    option.style.border = '1px solid #dadada';

    if (label && loader) {
      if (loading) {
        // hide label
        label.classList.add('opacity-0');
        // show loader
        loader.classList.remove('opacity-0');
      } else {
        // show label
        label.classList.remove('opacity-0');
        // hide loader
        loader.classList.add('opacity-0');
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'minicart-form',
        selector: '#minicart-form',
      },
    ];
  }
}

customElements.define('size-overlay', SizeOverlay);
